import React from 'react';
import { Link } from 'react-router-dom';
import { Stethoscope, MapPin, Phone, Mail, Facebook, Printer, Clock } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-gradient-to-br from-stone-50 to-stone-50 border-t border-stone-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Practice Info */}
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_premium-med-care/artifacts/57m0otz5_CFP_Logo-removebg-preview.png"
                alt="Crofton Family Practice Logo"
                className="h-10 w-10 object-contain"
                style={{ background: 'transparent' }} />

              <div>
                <h3 className="text-lg font-bold text-stone-900">
                  Crofton Family Practice
                </h3>
              </div>
            </div>
            <p className="text-sm text-stone-600 leading-relaxed">
              Where thoughtful care is still a tradition
            </p>
          </div>

          {/* Office Hours */}
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-4 flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              Office Hours
            </h4>
            <ul className="space-y-1.5">
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Mon</span>
                <span className="text-stone-600">9:00 AM - 4:15 PM</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Tue</span>
                <span className="text-stone-600">10:00 AM - 3:15 PM</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Wed</span>
                <span className="text-stone-600">9:00 AM - 4:15 PM</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Thu</span>
                <span className="text-stone-600">9:00 AM - 4:15 PM</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Fri</span>
                <span className="text-stone-600">9:00 AM - 12:00 PM</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Sat</span>
                <span className="text-stone-600 italic">Closed</span>
              </li>
              <li className="flex justify-between text-sm">
                <span className="text-stone-600 font-medium">Sun</span>
                <span className="text-stone-600 italic">Closed</span>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200">

                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200">

                  About Dr. Williams
                </Link>
              </li>
              <li>
                <Link
                  to="/services/traditional-primary-care"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200">

                  Traditional Primary Care
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200">

                  Contact Us
                </Link>
              </li>
              <li>
                <a
                  href="https://www.mdvip.com/doctors/allisonwilliamsmd"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-orange-500 hover:text-orange-600 transition-colors duration-200">

                  Join MDVIP
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/share/18fPjqYxpQ/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200 flex items-center space-x-2">
                  <Facebook className="h-4 w-4" />
                  <span>Follow Us on Facebook</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-sm font-semibold text-stone-900 mb-4">Contact Information</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-stone-600">
                  1684 Village Green Lower Level<br />
                  Crofton, MD 21114
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="h-5 w-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <a
                  href="tel:4107213822"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200">

                  (410) 721-3822
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Printer className="h-5 w-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-stone-600">
                  Fax: (410) 451-0960
                </span>
              </li>
              <li className="flex items-start space-x-3">
                <Mail className="h-5 w-5 text-stone-600 flex-shrink-0 mt-0.5" />
                <a
                  href="mailto:reception@villagegreenmd.com"
                  className="text-sm text-stone-600 hover:text-stone-600 transition-colors duration-200 break-all">

                  reception@villagegreenmd.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-6 border-t border-stone-200">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <p className="text-sm text-stone-600">
              © {currentYear} Crofton Family Practice. All rights reserved.
            </p>
            <p className="text-xs text-stone-500">
              Support services provided by MDVIP
            </p>
          </div>
        </div>
      </div>
    </footer>);

};

export default Footer;